import React from 'react';
import { Box, Skeleton, Paper, useTheme, alpha } from '@mui/material';
import { motion } from 'framer-motion';

interface SkeletonLoaderProps {
  variant?: 'metric-card' | 'chart' | 'timeline' | 'insight';
  count?: number;
  height?: number | string;
  delay?: number;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'metric-card',
  count = 1,
  height,
  delay = 0,
}) => {
  const theme = useTheme();

  const shimmerAnimation = {
    initial: { x: '-100%' },
    animate: { x: '100%' },
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay,
    },
  };

  const renderMetricCardSkeleton = () => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: height || 220,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        background: alpha(theme.palette.background.paper, 0.5),
      }}
    >
      {/* Shimmer effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.action.hover, 0.1)} 50%, transparent 100%)`,
          zIndex: 1,
        }}
        initial="initial"
        animate="animate"
        variants={shimmerAnimation}
      />

      {/* Header skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width={60} height={20} />
        </Box>
        <Skeleton variant="circular" width={24} height={24} />
      </Box>

      {/* Value skeleton */}
      <Box sx={{ my: 4 }}>
        <Skeleton variant="text" width={180} height={48} />
      </Box>

      {/* Footer skeleton */}
      <Box sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={80} height={20} />
        </Box>
      </Box>
    </Paper>
  );

  const renderChartSkeleton = () => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: height || 400,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        background: alpha(theme.palette.background.paper, 0.5),
      }}
    >
      {/* Shimmer effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.action.hover, 0.1)} 50%, transparent 100%)`,
          zIndex: 1,
        }}
        initial="initial"
        animate="animate"
        variants={shimmerAnimation}
      />

      {/* Header skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
      </Box>

      {/* Chart area skeleton */}
      <Box sx={{ height: 'calc(100% - 80px)', position: 'relative' }}>
        {/* Y-axis labels */}
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 40, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="text" width={30} height={16} />
          ))}
        </Box>

        {/* Chart bars/lines */}
        <Box sx={{ ml: 5, height: 'calc(100% - 40px)', display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width="100%"
              height={`${40 + Math.random() * 40}%`}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>

        {/* X-axis labels */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 40, right: 0, display: 'flex', justifyContent: 'space-between' }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="text" width={40} height={16} />
          ))}
        </Box>
      </Box>
    </Paper>
  );

  const renderTimelineSkeleton = () => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: height || 'auto',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        background: alpha(theme.palette.background.paper, 0.5),
      }}
    >
      {/* Shimmer effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.action.hover, 0.1)} 50%, transparent 100%)`,
          zIndex: 1,
        }}
        initial="initial"
        animate="animate"
        variants={shimmerAnimation}
      />

      {/* Timeline items */}
      {[...Array(4)].map((_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Skeleton variant="circular" width={40} height={40} />
            {i < 3 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: 40,
                  width: 2,
                  height: 60,
                  transform: 'translateX(-50%)',
                  backgroundColor: alpha(theme.palette.divider, 0.2),
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, pt: 1 }}>
            <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="80%" height={16} />
          </Box>
        </Box>
      ))}
    </Paper>
  );

  const renderInsightSkeleton = () => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: height || 'auto',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        background: alpha(theme.palette.background.paper, 0.5),
      }}
    >
      {/* Shimmer effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.action.hover, 0.1)} 50%, transparent 100%)`,
          zIndex: 1,
        }}
        initial="initial"
        animate="animate"
        variants={shimmerAnimation}
      />

      {/* Header skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="text" width={100} height={20} />
      </Box>

      {/* Content skeleton */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={16} />
        <Skeleton variant="text" width="90%" height={16} />
        <Skeleton variant="text" width="70%" height={16} />
      </Box>

      {/* Action skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" width={120} height={36} />
      </Box>
    </Paper>
  );

  const renderSkeleton = () => {
    switch (variant) {
      case 'chart':
        return renderChartSkeleton();
      case 'timeline':
        return renderTimelineSkeleton();
      case 'insight':
        return renderInsightSkeleton();
      default:
        return renderMetricCardSkeleton();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay }}
    >
      {count === 1 ? (
        renderSkeleton()
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          {[...Array(count)].map((_, index) => (
            <Box key={index}>{renderSkeleton()}</Box>
          ))}
        </Box>
      )}
    </motion.div>
  );
};

export default SkeletonLoader;